import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row } from 'components/Row';
import Loader from 'components/Loader';
import Spacing from 'components/Spacing';
import { Button } from 'components/Button';
import { showToast, Toast } from 'components/Toast';
import { InputSelect } from 'components/InputSelect';
import { H1, UtilityText } from 'components/Typography';
import Input, { onChangeTextareaType, onChangeType } from 'components/Input';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getUrlParams } from 'hooks/utils';
import { handleSession } from 'hooks/handleSession';
import { getUserId } from 'services/storage';
import { uploadToCloudinary } from 'services/cloudinary';
import { getFaqsState } from 'selectors/faqs';
import { getSeriesState } from 'selectors/series';
import { getCuadrosState } from 'selectors/cuadros';
import { getFaqs, insertFaq, updateFaqs } from 'store/faqs/actions';
import { getSeries, insertSerie, updateSeries } from 'store/series/actions';
import { getCuadros, insertCuadro, updateCuadros } from 'store/cuadros/actions';
import {
  Container,
  FormContainer,
  InputSelectContainer,
  BackButton,
  ImageUploadSection,
  SectionLabel,
  ImagePreview,
  ExtraImageList,
  ExtraImageThumb,
  ExtraImageImg,
  RemoveImageBtn,
  UploadLabel,
} from './styled';
import { getBio, updateBio } from 'store/bio/actions';
import { getBioState } from 'selectors/bio';

export const PageDetailScreen = () => {
  const form: any = useRef();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isExpired } = handleSession();
  const { urlPage, urlId } = getUrlParams();

  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [label, setLabel] = useState('');
  const [price, setPrice] = useState('');
  const [discount, setDiscount] = useState(0);
  const [extraImages, setExtraImages] = useState<any>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [extraImageUrls, setExtraImageUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [order, setOrder] = useState(0);
  const [idSerie, setIdSerie] = useState<number>(0);
  const [buttonPressed, setButtonPressed] = useState(false);
  const [seriesOptions, setSeriesOptions] = useState(Array<{ value: string; label: string }>());
  const [desc, setDesc] = useState('');
  const [title2, setTitle2] = useState('');
  const [desc2, setDesc2] = useState('');
  const [title3, setTitle3] = useState('');

  const { faqsState, seriesState, cuadrosState, bioState } = useAppSelector(state => ({
    faqsState: getFaqsState(state),
    seriesState: getSeriesState(state),
    cuadrosState: getCuadrosState(state),
    bioState: getBioState(state),
  }));

  const getTitle = () => {
    let page = '';
    if (urlPage === 'series') page = 'serie';
    else if (urlPage === 'cuadros') page = 'cuadro';
    else if (urlPage === 'bio') page = 'biografía';
    else page = 'pregunta frecuente';
    return `${urlId ? 'Editar ' : 'Crear '}${page}`;
  };

  useEffect(() => {
    if (urlId) {
      if (!getUserId() || isExpired()) navigate('/admin');
      if (!faqsState.data) dispatch(getFaqs());
      if (!cuadrosState.data) dispatch(getCuadros());
      if (!seriesState.data) dispatch(getSeries());
      if (!bioState.data) dispatch(getBio());
    }
  }, []);

  useEffect(() => {
    if (urlId && urlPage === 'faqs' && !faqsState.isLoading && faqsState.data) {
      const element = faqsState.data?.find(item => item.id === parseInt(urlId) && item.active);
      if (element) {
        setQuestion(element.question);
        setAnswer(element.answer);
      } else {
        navigate('/dashboard');
      }
    }
  }, [faqsState]);

  useEffect(() => {
    if (!seriesState.isLoading && seriesState.data) {
      if (urlId && urlPage === 'series') {
        const element = seriesState.data?.find(item => item.id === parseInt(urlId) && item.active);
        if (element) {
          setTitle(element.title);
          setOrder(element.order || 0);
        } else navigate('/dashboard');
      }
      setSeriesOptions(
        seriesState.data.map(serie => ({ value: serie.id.toString(), label: serie.title }))
      );
    }
  }, [seriesState]);

  useEffect(() => {
    if (urlId && urlPage === 'cuadros' && !cuadrosState.isLoading && cuadrosState.data) {
      const element = cuadrosState.data.find(item => item.id === parseInt(urlId) && item.active);
      if (element) {
        setName(element.name);
        setDescription(element.description);
        setIdSerie(seriesState.data?.find(serie => serie.id === element.id_serie)?.id || 0);
        setLabel(element.label);
        setPrice(element.price);
        setDiscount(element.discount || 0);
        setExtraImages(element.extra_images);
        setImageUrl(element.image_url || '');
        setExtraImageUrls(element.extra_image_urls || []);
        setOrder(element.order || 0);
      } else {
        navigate('/dashboard');
      }
    }
  }, [cuadrosState]);

  useEffect(() => {
    if (urlId && urlPage === 'bio' && !bioState.isLoading && bioState.data) {
      const element = bioState.data?.find(item => item.id === parseInt(urlId));
      if (element) {
        setTitle(element.title);
        setDesc(element.desc);
        setTitle2(element.title2);
        setDesc2(element.desc2);
        setTitle3(element.title3);
      } else {
        navigate('/dashboard');
      }
    }
  }, [bioState]);

  const showSuccessAndNavigate = () => {
    showToast('Cambio realizado exitosamente!', 'success');
    setTimeout(() => {
      navigate(`/dashboard${urlPage === 'bio' ? '' : `?p=${urlPage}`}`);
      setButtonPressed(false);
    }, 3000);
  };

  const showError = () => {
    showToast('Debe completar todos los campos obligatorios', 'error');
    setButtonPressed(false);
  };

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setImageUrl(url);
    } catch {
      showToast('Error al subir la imagen', 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleExtraImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setExtraImageUrls(prev => [...prev, url]);
    } catch {
      showToast('Error al subir la imagen', 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const removeExtraImage = (index: number) => {
    setExtraImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    setButtonPressed(true);
    if (urlPage === 'series') {
      if (title) {
        if (urlId) dispatch(updateSeries(parseInt(urlId), title, order));
        else dispatch(insertSerie(title, order));
        showSuccessAndNavigate();
      } else showError();
    }

    if (urlPage === 'faqs') {
      if (question && answer) {
        if (urlId) dispatch(updateFaqs(parseInt(urlId), question, answer));
        else dispatch(insertFaq(question, answer));
        showSuccessAndNavigate();
      } else showError();
    }

    if (urlPage === 'cuadros') {
      if (name && price) {
        if (urlId)
          dispatch(
            updateCuadros(
              parseInt(urlId),
              name,
              description,
              idSerie,
              label,
              price,
              discount,
              extraImages,
              extraImageUrls,
              imageUrl,
              order
            )
          );
        else
          dispatch(
            insertCuadro(
              name,
              description,
              idSerie,
              label,
              price,
              discount,
              extraImages,
              extraImageUrls,
              imageUrl,
              order
            )
          );
        showSuccessAndNavigate();
      } else showError();
    }

    if (urlPage === 'bio') {
      if (title && desc && title2 && desc2 && title3) {
        if (urlId) dispatch(updateBio(parseInt(urlId), title, desc, title2, desc2, title3));
        showSuccessAndNavigate();
      } else showError();
    }
  };

  const backPath = `/dashboard${urlPage === 'bio' ? '' : `?p=${urlPage}`}`;

  return (
    <>
      <Toast />
      <Container>
        <BackButton onClick={() => navigate(backPath)}>← Volver</BackButton>
        <H1>{getTitle()}</H1>
        <Spacing $size={20} />
        <FormContainer ref={form}>
          {urlPage === 'series' && (
            <>
              <Input
                id="title"
                placeholder="Titulo *"
                value={title}
                textOnChange={(e: onChangeType) => setTitle(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="order"
                placeholder="Orden"
                type="number"
                value={order}
                textOnChange={(e: onChangeType) => setOrder(parseInt(e.target.value))}
                fullWidth
              />
            </>
          )}
          {urlPage === 'faqs' && (
            <>
              <Input
                id="question"
                placeholder="Pregunta *"
                value={question}
                textareaOnChange={(e: onChangeTextareaType) => setQuestion(e.target.value)}
                fullWidth
                textarea
              />
              <Spacing $size={16} />
              <Input
                id="answer"
                placeholder="Respuesta *"
                value={answer}
                textareaOnChange={(e: onChangeTextareaType) => setAnswer(e.target.value)}
                fullWidth
                textarea
              />
            </>
          )}
          {urlPage === 'cuadros' && (
            <>
              <Input
                id="name"
                placeholder="Nombre *"
                value={name}
                textOnChange={(e: onChangeType) => setName(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="description"
                placeholder="Descripción"
                value={description}
                textareaOnChange={(e: onChangeTextareaType) => setDescription(e.target.value)}
                fullWidth
                textarea
              />
              <Spacing $size={16} />
              <InputSelectContainer>
                <UtilityText>Serie *</UtilityText>
                <InputSelect
                  options={seriesOptions}
                  onChange={item => setIdSerie(item.value)}
                  value={seriesOptions.find(serie => serie.value === idSerie.toString())}
                />
              </InputSelectContainer>
              <Spacing $size={16} />
              <Input
                id="label"
                placeholder="Etiqueta"
                value={label}
                textOnChange={(e: onChangeType) => setLabel(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="price"
                placeholder="Precio *"
                value={price}
                textOnChange={(e: onChangeType) => setPrice(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="discount"
                placeholder="Descuento"
                type="number"
                value={discount}
                textOnChange={(e: onChangeType) => setDiscount(parseInt(e.target.value))}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="order"
                placeholder="Orden"
                type="number"
                value={order}
                textOnChange={(e: onChangeType) => setOrder(parseInt(e.target.value))}
                fullWidth
              />
              <Spacing $size={16} />
              <ImageUploadSection>
                <SectionLabel>Imagen principal</SectionLabel>
                {imageUrl && <ImagePreview src={imageUrl} alt="Principal" />}
                <UploadLabel $disabled={isUploading}>
                  {isUploading ? 'Subiendo...' : imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    disabled={isUploading}
                  />
                </UploadLabel>
              </ImageUploadSection>
              <Spacing $size={16} />
              <ImageUploadSection>
                <SectionLabel>Imágenes adicionales</SectionLabel>
                {extraImageUrls.length > 0 && (
                  <ExtraImageList>
                    {extraImageUrls.map((url, i) => (
                      <ExtraImageThumb key={i}>
                        <ExtraImageImg src={url} alt={`Extra ${i + 1}`} />
                        <RemoveImageBtn type="button" onClick={() => removeExtraImage(i)}>
                          ×
                        </RemoveImageBtn>
                      </ExtraImageThumb>
                    ))}
                  </ExtraImageList>
                )}
                <UploadLabel $disabled={isUploading}>
                  {isUploading ? 'Subiendo...' : '+ Agregar imagen'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleExtraImageUpload}
                    disabled={isUploading}
                  />
                </UploadLabel>
              </ImageUploadSection>
            </>
          )}
          {urlPage === 'bio' && (
            <>
              <Input
                id="title"
                placeholder="Titulo 1 *"
                value={title}
                textOnChange={(e: onChangeType) => setTitle(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="desc"
                placeholder="Descripción 1"
                value={desc}
                textareaOnChange={(e: onChangeTextareaType) => setDesc(e.target.value)}
                fullWidth
                textarea
              />
              <Spacing $size={16} />
              <Input
                id="title2"
                placeholder="Titulo 2 *"
                value={title2}
                textOnChange={(e: onChangeType) => setTitle2(e.target.value)}
                fullWidth
              />
              <Spacing $size={16} />
              <Input
                id="desc2"
                placeholder="Descripción 2"
                value={desc2}
                textareaOnChange={(e: onChangeTextareaType) => setDesc2(e.target.value)}
                fullWidth
                textarea
              />
              <Spacing $size={16} />
              <Input
                id="title3"
                placeholder="Titulo 3 *"
                value={title3}
                textOnChange={(e: onChangeType) => setTitle3(e.target.value)}
                fullWidth
              />
            </>
          )}
        </FormContainer>
        <Button onClick={handleSubmit} style={{ width: 'auto' }}>
          <Row>
            {urlId ? 'Editar' : 'Crear'}
            {buttonPressed && (
              <>
                <Spacing $vertical $size={16} />
                <Loader type="spinner" width={16} height={16} />
              </>
            )}
          </Row>
        </Button>
      </Container>
    </>
  );
};
